import '.jw{margin-left:2.5rem;}.ssr-plugin'
import '.ie{padding-top:0.5rem;}.ssr-plugin'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { PostActivityRow } from './PostActivityRow'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthState } from '@based/sdk/react'
import { PostMediaGallery } from '../../mediaGallery/PostMediaGallery'
import { styled } from 'inlines'
import useDateFormat from '@app/util/useDateFormat'
import { PostProps } from './models'
import { ReplyTextBox } from './ReplyTextBox'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { LoadMoreCommentsButton } from './LoadMoreButton'

export type CommentProps = Omit<PostProps, 'onLoadMoreComments'> & {
  level: 'comment' | 'reply'
  loadMoreComments?: () => void
}

export const Comment = ({
  level,
  post: {
    id,
    authorName,
    authorAvatar,
    creationDate,
    content,
    media,
    reactionUserIds,
    comments,
  },
  onReply,
  onReact,
  loadMoreComments,
}: CommentProps) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.post
  const authState = useAuthState()
  const [replyInputShown, setReplyInputShown] = useState(false)
  const [mounted, setMounted] = useState(false)

  const postReplyRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  const hasReaction = !!(
    mounted &&
    authState.userId &&
    reactionUserIds.includes(authState.userId.toString())
  )
  const dateFormat = useDateFormat(true)

  const onReplyHandler = useCallback(() => {
    setReplyInputShown(true)
    if (postReplyRef.current) {
      postReplyRef.current.focus()
    }
  }, [postReplyRef])

  const onSubmitHandler = useCallback(
    (text: string) => {
      setReplyInputShown(false)
      return onReply(id, text)
    },
    [id, onReply],
  )

  useEffect(() => {
    if (replyInputShown && postReplyRef.current) {
      postReplyRef.current.focus()
    }
  }, [replyInputShown])

  return (
    <Column
      gap="0.5rem"
      alignItems="stretch"
      style={{
        paddingTop: '1rem',
        borderTop: '1px solid var(--gray-100)',
      }}
    >
      <Column
        gap="0.25rem"
        style={{ marginBottom: '0.5rem' }}
        alignItems="stretch"
      >
        <Row
          gap="0.5rem"
          style={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Row gap="0.5rem" style={{ alignItems: 'center' }}>
            <Avatar
              size="extra-small"
              image={authorAvatar}
              labelSource={authorName}
            />
            <Text.Medium isBold={true}>{authorName}</Text.Medium>
          </Row>
          <Text.Small style={{ color: 'var(--gray-300)' }}>
            {dateFormat(creationDate)}
          </Text.Small>
        </Row>
        <Column
          alignItems="stretch"
          style={{ marginLeft: '2.5rem', gap: '0.62rem' }}
        >
          <Text.Medium style={{ lineHeight: '1.75rem' }}>{content}</Text.Medium>
          <PostMediaGallery images={media} />
          <PostActivityRow
            version={level}
            hasReaction={hasReaction}
            reactions={reactionUserIds.length}
            comments={comments.length}
            onReactionClick={() => onReact(id)}
            onReplyClick={onReplyHandler}
          />
        </Column>
      </Column>
      <div className="jw">
        {level === 'comment' &&
          comments.map((comment, index) => (
            <Comment
              key={index}
              post={comment}
              level="reply"
              onReact={onReact}
              onReply={onReply}
            />
          ))}
        {level === 'comment' && loadMoreComments && (
          <LoadMoreCommentsButton
            label={translations.loadMoreReplies}
            onClick={loadMoreComments}
          />
        )}
      </div>

      {replyInputShown && (
        <div className="ie jw">
          <ReplyTextBox
            ref={postReplyRef}
            onSumbit={onSubmitHandler}
            onBlur={() => setReplyInputShown(false)}
          />
        </div>
      )}
    </Column>
  )
}
